import {
  Button,
  Form,
  Input,
  Modal,
  Popconfirm,
  Space,
  Table,
  Tag,
  Typography,
  message,
} from "antd";
import { PlusOutlined, StarFilled } from "@ant-design/icons";
import { useCallback, useEffect, useState } from "react";
import {
  addCompanyContact,
  deleteCompanyContact,
  getCompanyContacts,
  updateCompanyContact,
} from "../../repository/data.repository";
import { CompanyContact } from "../../repository/data.default";
import { QueryOperation } from "../../code/search-query/search-query";
import { nameOf } from "../../utils/typescript";
import { getProblemMessage, retry } from "../../utils/http";

export interface CompanyContactsPanelProps {
  readonly companyUid: string | undefined;
  readonly open: boolean;
}

// Admin brief 2026-11-04 — full contacts list. The primary contact on the
// Company record (ContactPerson / Email2 / PhoneNumber2 shortcut fields) stays
// as a one-field convenience; this panel adds the "many contacts per company"
// list the admin asked for on Nov 3.
function CompanyContactsPanel(props: CompanyContactsPanelProps) {
  const [items, setItems] = useState<CompanyContact[]>([]);
  const [loading, setLoading] = useState(false);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editorValue, setEditorValue] = useState<CompanyContact | null>(null);

  const load = useCallback(() => {
    if (!props.companyUid) return;
    setLoading(true);
    retry(() =>
      getCompanyContacts({
        searchQuery: {
          conditions: [
            {
              field: nameOf<CompanyContact>("companyUid"),
              operation: QueryOperation.Equal,
              values: [props.companyUid!],
            },
          ],
        },
        pageNumber: 0,
        pageSize: 100,
      }),
    )
      .then(({ data }) => setItems(data))
      .catch((err) =>
        message.error(getProblemMessage(err) || "Failed to load contacts."),
      )
      .finally(() => setLoading(false));
  }, [props.companyUid]);

  useEffect(() => {
    if (props.open && props.companyUid) load();
  }, [props.open, props.companyUid, load]);

  function onAdd(): void {
    setEditorValue(null);
    setEditorOpen(true);
  }

  function onEdit(row: CompanyContact): void {
    setEditorValue(row);
    setEditorOpen(true);
  }

  async function onDelete(uid: string): Promise<void> {
    try {
      await deleteCompanyContact(uid);
      message.success("Contact archived.");
      load();
    } catch (err) {
      message.error(getProblemMessage(err) || "Failed to archive contact.");
    }
  }

  if (!props.companyUid) {
    return (
      <Typography.Text type="secondary" className="text-xs">
        Save the company first — then you can add contacts.
      </Typography.Text>
    );
  }

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-between">
        <Typography.Text strong>Contacts</Typography.Text>
        <Button
          size="small"
          type="dashed"
          icon={<PlusOutlined />}
          onClick={onAdd}
        >
          Add contact
        </Button>
      </div>
      <Table
        size="small"
        rowKey={(r) => r.uid}
        dataSource={items}
        loading={loading}
        pagination={items.length > 10 ? { pageSize: 10, size: "small" } : false}
        locale={{ emptyText: "No additional contacts yet." }}
        columns={[
          {
            title: "Name",
            dataIndex: "name",
            render: (v: string, row: CompanyContact) => (
              <Space size={4}>
                {row.isPrimary && (
                  <Tag color="gold" icon={<StarFilled />}>
                    Primary
                  </Tag>
                )}
                <span>{v}</span>
              </Space>
            ),
          },
          { title: "Role", dataIndex: "role" },
          { title: "Email", dataIndex: "email" },
          { title: "Phone", dataIndex: "phoneNumber" },
          {
            title: "",
            key: "actions",
            width: 140,
            render: (_: any, row: CompanyContact) => (
              <Space size={4}>
                <Button size="small" type="link" onClick={() => onEdit(row)}>
                  Edit
                </Button>
                <Popconfirm
                  title="Archive this contact?"
                  description="It will be hidden from the list."
                  okText="Archive"
                  okButtonProps={{ danger: true }}
                  onConfirm={() => onDelete(row.uid)}
                >
                  <Button size="small" type="link" danger>
                    Archive
                  </Button>
                </Popconfirm>
              </Space>
            ),
          },
        ]}
      />
      <ContactEditorDialog
        open={editorOpen}
        companyUid={props.companyUid}
        value={editorValue}
        onCancel={() => setEditorOpen(false)}
        onSaved={() => {
          setEditorOpen(false);
          load();
        }}
      />
    </div>
  );
}

function ContactEditorDialog(props: {
  open: boolean;
  companyUid: string;
  value: CompanyContact | null;
  onCancel: () => void;
  onSaved: () => void;
}) {
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const isEdit = !!props.value;

  useEffect(() => {
    if (!props.open) return;
    if (props.value) {
      form.setFieldsValue({
        name: props.value.name,
        role: props.value.role,
        email: props.value.email,
        phoneNumber: props.value.phoneNumber,
        isPrimary: props.value.isPrimary,
        notes: props.value.notes,
      });
    } else {
      form.resetFields();
    }
  }, [props.open, props.value, form]);

  async function onSubmit(): Promise<void> {
    try {
      const v = await form.validateFields();
      setSaving(true);
      const payload = {
        name: v.name,
        role: v.role,
        email: v.email,
        phoneNumber: v.phoneNumber,
        isPrimary: !!v.isPrimary,
        notes: v.notes,
        companyUid: props.companyUid,
      };
      if (isEdit) {
        await updateCompanyContact({
          uid: props.value!.uid,
          rowVersion: props.value!.rowVersion,
          ...payload,
        });
        message.success("Contact updated.");
      } else {
        await addCompanyContact(payload);
        message.success("Contact added.");
      }
      setSaving(false);
      props.onSaved();
    } catch (err: any) {
      setSaving(false);
      if (err?.errorFields) return;
      message.error(getProblemMessage(err) || "Failed to save contact.");
    }
  }

  return (
    <Modal
      open={props.open}
      title={isEdit ? "Edit contact" : "Add contact"}
      onCancel={props.onCancel}
      onOk={onSubmit}
      confirmLoading={saving}
      okText={isEdit ? "Save" : "Add"}
      destroyOnClose
    >
      <Form form={form} layout="vertical">
        <Form.Item
          name="name"
          label="Name"
          rules={[{ required: true, message: "Name is required" }]}
        >
          <Input />
        </Form.Item>
        <Form.Item name="role" label="Role / Position (optional)">
          <Input />
        </Form.Item>
        <Form.Item
          name="email"
          label="Email (optional)"
          rules={[{ type: "email", message: "Invalid email" }]}
        >
          <Input />
        </Form.Item>
        <Form.Item name="phoneNumber" label="Phone (optional)">
          <Input />
        </Form.Item>
        <Form.Item
          name="isPrimary"
          valuePropName="checked"
          label="Mark as primary contact"
          tooltip="Only one contact per company can be primary. Flipping this clears the flag on the previous primary."
        >
          <input type="checkbox" />
        </Form.Item>
        <Form.Item name="notes" label="Notes (optional)">
          <Input.TextArea rows={2} />
        </Form.Item>
      </Form>
    </Modal>
  );
}

export default CompanyContactsPanel;
